import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type Cmd = {
    id: string;
    label: string;
    to: string;
    keywords?: string;
    section?: string;
};

export default function QuickSearchInline({ projectId, weekOf }: { projectId: string; weekOf?: string }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const nav = useNavigate();
    const loc = useLocation();
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [activeIdx, setActiveIdx] = useState(0);

    // 동일한 커맨드 소스 (CommandPalette와 일치)
    const cmds: Cmd[] = useMemo(
        () => [
            { id: 'wbs', label: 'WBS Editor', to: `/projects/${projectId}/wbs`, section: 'Pages' },
            { id: 'tasks', label: 'Tasks', to: `/projects/${projectId}/tasks`, section: 'Pages' },
            { id: 'risks', label: 'Risks', to: `/projects/${projectId}/risks`, section: 'Pages' },
            {
                id: 'gantt',
                label: 'Gantt Chart',
                to: `/projects/${projectId}/gantt-chart${weekOf ? `?weekOf=${weekOf}` : ''}`,
                section: 'Pages',
            },
            { id: 'dashboard', label: 'Dashboard', to: `/projects/${projectId}`, section: 'Pages' },

            // Tools / Actions 예시
            {
                id: 'approvals',
                label: 'Open Approval Queue',
                to: `${loc.pathname}#open-approvals`,
                section: 'Tools',
                keywords: 'approval ev pending',
            },
            { id: 'newTask', label: 'New Task…', to: `${loc.pathname}#new-task`, section: 'Tools' },
        ],
        [projectId, weekOf, loc.pathname],
    );

    const filtered = useMemo(() => {
        const qn = q.trim().toLowerCase();
        if (!qn) return cmds;
        return cmds.filter((c) => c.label.toLowerCase().includes(qn) || c.keywords?.toLowerCase().includes(qn));
    }, [cmds, q]);

    // 입력 포커스/블러 핸들링
    const blurTimer = useRef<number | null>(null);
    const safeClose = () => {
        // 클릭 선택 허용 위해 약간 지연 후 닫기
        blurTimer.current = window.setTimeout(() => setOpen(false), 100);
    };
    useEffect(
        () => () => {
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
        },
        [],
    );

    // 키보드 내비게이션
    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
            return;
        }
        if (!filtered.length) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIdx((i) => (i + 1) % filtered.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const target = filtered[activeIdx];
            if (target) {
                nav(target.to);
                setOpen(false);
                inputRef.current?.blur();
            }
        } else if (e.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
        }
    };

    useEffect(() => {
        // 목록이 바뀌면 활성 인덱스 리셋
        setActiveIdx(0);
    }, [q, open]);

    return (
        <div className="relative w-full min-w-0">
            <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => setOpen(true)}
                onBlur={safeClose}
                placeholder="Quick search…"
                aria-label="Quick search"
                className="w-full rounded-lg border px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
            {open && (
                <ul
                    ref={listRef}
                    role="listbox"
                    className="absolute z-[60] mt-1 w-full max-h-72 overflow-auto rounded-xl border bg-white shadow-lg"
                >
                    {filtered.map((c, idx) => (
                        <li key={c.id}>
                            <button
                                type="button"
                                role="option"
                                aria-selected={idx === activeIdx}
                                className={`w-full text-left px-3 py-2 text-sm ${
                                    idx === activeIdx ? 'bg-gray-100' : 'hover:bg-gray-50'
                                }`}
                                onMouseEnter={() => setActiveIdx(idx)}
                                onMouseDown={(e) => e.preventDefault()} // blur 전에 클릭 처리
                                onClick={() => {
                                    nav(c.to);
                                    setOpen(false);
                                    inputRef.current?.blur();
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <span>{c.label}</span>
                                    <span className="text-[11px] text-gray-500">{c.section}</span>
                                </div>
                            </button>
                        </li>
                    ))}
                    {filtered.length === 0 && <li className="px-3 py-4 text-sm text-gray-500">No results</li>}
                </ul>
            )}
        </div>
    );
}
